import { supabase } from '../lib/supabaseClient';
import { GrilleHopitalIDERow, GradeData, EchelonData } from '../types';

/**
 * @fileoverview Service for fetching and transforming grille_Hopital_IDE data from Supabase
 * 
 * This service handles all interactions with the grille_Hopital_IDE table in Supabase,
 * including data fetching, error handling, and transformation to the application's
 * expected data structure.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

/**
 * Fetches all grille data from the specified Supabase table
 * 
 * @param {string} tableName - Name of the Supabase table to fetch from
 * @returns {Promise<GrilleHopitalIDERow[]>} Array of raw grille data from Supabase
 * @throws {Error} If the fetch operation fails
 */
export async function fetchGrilleData(tableName: string): Promise<GrilleHopitalIDERow[]> {
  try {
    console.log('🔍 grilleHopitalService - fetchGrilleData called for table:', tableName);
    
    const { data, error } = await supabase
      .from(tableName)
      .select('*, echelon_range_label')
      .order('grade_id', { ascending: true })
      .order('echelon', { ascending: true });

    if (error) {
      console.error(`Supabase error fetching ${tableName}:`, error);
      throw new Error(`Failed to fetch grille data: ${error.message}`);
    }

    if (!data) {
      throw new Error(`No data returned from ${tableName} table`);
    }

    console.log(`🔍 grilleHopitalService - Successfully fetched ${data.length} grille records from ${tableName}`);
    console.log('🔍 grilleHopitalService - Raw data sample:', data.slice(0, 3));
    console.log('🔍 grilleHopitalService - Raw data first row full:', data[0]);
    console.log('🔍 grilleHopitalService - All echelon values:', data.map(row => row.echelon).sort((a, b) => a - b));
    return data;
  } catch (error) {
    console.error(`Error in fetchGrilleData for ${tableName}:`, error);
    throw error;
  }
}

/**
 * Transforms raw Supabase data into the application's GradeData structure
 * 
 * This function groups the flat table data by grade and transforms it into
 * the hierarchical structure expected by the RemunerationIndiciaire component.
 * 
 * @param {GrilleHopitalIDERow[]} rawData - Raw data from Supabase
 * @returns {GradeData[]} Transformed data grouped by grade
 * 
 * @example
 * const rawData = await fetchGrilleHopitalIDEData();
 * const grades = transformGrilleDataToGrades(rawData);
 * console.log(grades); // [{ id: 1, nom: "Grade 1...", echelons: [...] }, ...]
 */
export function transformGrilleDataToGrades(rawData: GrilleHopitalIDERow[]): GradeData[] {
  console.log('🔍 grilleHopitalService - transformGrilleDataToGrades called with', rawData.length, 'rows');
  console.log('🔍 grilleHopitalService - Raw data sample for debugging:', rawData.slice(0, 5));
  
  // Group data by grade_id
  const gradeMap = new Map<number, GrilleHopitalIDERow[]>();
  
  rawData.forEach(row => {
    if (!gradeMap.has(row.grade_id)) {
      gradeMap.set(row.grade_id, []);
    }
    gradeMap.get(row.grade_id)!.push(row);
  });

  console.log('🔍 grilleHopitalService - gradeMap created:', gradeMap);

  // Transform to GradeData structure
  const grades: GradeData[] = [];
  
  gradeMap.forEach((rows, gradeId) => {
    if (rows.length === 0) return;
    
    console.log('🔍 grilleHopitalService - Processing grade', gradeId, 'with', rows.length, 'rows');
    console.log('🔍 grilleHopitalService - Raw rows for grade', gradeId, ':', rows.map(r => ({
      echelon: r.echelon,
      indice_brut: r.indice_brut,
      indice_majore: r.indice_majore,
      echelon_label: r.echelon_label,
      echelon_range_label: r.echelon_range_label
    })));
    
    // Use the first row to get grade information
    const firstRow = rows[0];
    
    // Transform echelons
    const echelons: EchelonData[] = rows
      .filter(row => {
        const echelonNum = Number(row.echelon);
        const indiceBrut = Number(row.indice_brut);
        const indiceMajore = Number(row.indice_majore);
        
        console.log('🔍 grilleHopitalService - Filtering echelon for grade', gradeId, ':', {
          echelonNum,
          indiceBrut,
          indiceMajore,
          echelon_label: row.echelon_label,
          echelon_range_label: row.echelon_range_label,
          duree_avancement: row.duree_avancement
        });
        
        // Filter out invalid data - allow all valid echelons including negative and zero
        const isValid = (
          !isNaN(echelonNum) &&
          !isNaN(indiceBrut) && 
          indiceBrut > 0 &&
          !isNaN(indiceMajore) && 
          indiceMajore > 0
        );
        
        console.log('🔍 grilleHopitalService - Echelon', echelonNum, 'for grade', gradeId, 'is valid:', isValid, 'reasons:', {
          echelonIsNumber: !isNaN(echelonNum),
          indiceBrutIsNumber: !isNaN(indiceBrut),
          indiceBrutPositive: indiceBrut > 0,
          indiceMajoreIsNumber: !isNaN(indiceMajore),
          indiceMajorePositive: indiceMajore > 0
        });
        return isValid;
      })
      .map(row => ({
        echelon: Number(row.echelon),
        indice: Number(row.indice_brut),
        indiceMajoré: Number(row.indice_majore),
        dureeAvancement: row.duree_avancement,
        echelonLabel: row.echelon_label || getEchelonDisplayLabel(Number(row.echelon)),
        echelonRangeLabel: row.echelon_range_label
      }));
    
    console.log('🔍 grilleHopitalService - Echelons after filtering for grade', gradeId, ':', echelons);
    
    // Sort echelons by echelon number
    echelons.sort((a, b) => a.echelon - b.echelon);
    
    console.log('🔍 grilleHopitalService - Echelons after sorting for grade', gradeId, ':', echelons);
    
    grades.push({
      id: Number(gradeId),
      nom: firstRow.grade_nom,
      description: firstRow.grade_description,
      echelons
    });
  });
  
  // Sort grades by id
  grades.sort((a, b) => a.id - b.id);
  
  console.log(`🔍 grilleHopitalService - Transformed ${rawData.length} rows into ${grades.length} grades`);
  console.log('🔍 grilleHopitalService - Final grades data:', grades);
  console.log('🔍 grilleHopitalService - First grade detailed:', {
    id: grades[0]?.id,
    nom: grades[0]?.nom,
    echelonsCount: grades[0]?.echelons?.length || 0,
    firstEchelon: grades[0]?.echelons?.[0]
  });
  return grades;
}

/**
 * Gets the display label for an echelon, handling temporary echelons
 * 
 * @param {number} echelon - The echelon number
 * @returns {string | undefined} The display label or undefined for positive echelons
 */
function getEchelonDisplayLabel(echelon: number): string | undefined {
  switch (echelon) {
    case -1:
      return '(1)';
    case 0:
      return '(2)';
    default:
      return undefined; // Use default "Échelon X" format for positive echelons
  }
}
/**
 * Fetches and transforms grille data from the specified table in one operation
 * 
 * This is a convenience function that combines fetching and transformation
 * into a single operation for easier use in React components.
 * 
 * @param {string} tableName - Name of the Supabase table to fetch from
 * @returns {Promise<GradeData[]>} Transformed grade data ready for use
 * @throws {Error} If fetch or transformation fails
 * 
 * @example
 * const grades = await getGrilleData('grille_Hopital_IDE');
 * // Use grades directly in your component
 */
export async function getGrilleData(tableName: string): Promise<GradeData[]> {
  try {
    const rawData = await fetchGrilleData(tableName);
    return transformGrilleDataToGrades(rawData);
  } catch (error) {
    console.error(`Error in getGrilleData for ${tableName}:`, error);
    throw error;
  }
}

/**
 * Validates that the fetched grille data is complete and consistent
 * 
 * @param {GradeData[]} grades - Transformed grade data to validate
 * @returns {boolean} True if data is valid, false otherwise
 */
export function validateGrilleData(grades: GradeData[]): boolean {
  if (!grades || grades.length === 0) {
    console.warn('🔍 grilleHopitalService - validateGrilleData: No grades data available');
    return false;
  }

  console.log('🔍 grilleHopitalService - validateGrilleData: Validating', grades.length, 'grades');

  for (const grade of grades) {
    console.log('🔍 grilleHopitalService - validateGrilleData: Checking grade', grade.id, 'with', grade.echelons?.length || 0, 'echelons');
    
    if (!grade.echelons || grade.echelons.length === 0) {
      console.warn(`🔍 grilleHopitalService - validateGrilleData: Grade ${grade.id} has no echelons`);
      return false;
    }
    
    // Check that each echelon has valid data
    for (const echelon of grade.echelons) {
      console.log('🔍 grilleHopitalService - validateGrilleData: Checking echelon', echelon.echelon, 'with indiceMajoré:', echelon.indiceMajoré);
      
      if (!echelon.indiceMajoré || echelon.indiceMajoré <= 0) {
        console.warn(`🔍 grilleHopitalService - validateGrilleData: Grade ${grade.id}, Echelon ${echelon.echelon} has invalid indice majoré:`, echelon.indiceMajoré);
        return false;
      }
    }
  }

  console.log('🔍 grilleHopitalService - validateGrilleData: Validation passed');
  return true;
}